/**
 * Clone to lines sheet (W2-11 batch-tools; DESIGN.md §4.14, §5.8; RPE event cloning CLONE-01..06, E§4.10).
 * Targets are picked lines (ids, so reordering lines later changes nothing) or N new lines; per-target time stagger
 * and value ramps for X, Y, rotation and opacity (the Distribute engine); overlap policy; notes; keep source.
 * Live ghosts through `useToolGesture`. The ramps in use are shown as their equivalent expressions (`rampExpression`
 * with Add: u runs over the targets, 0 for the first and 1 for the last).
 */

import { useMemo, useState } from 'react';
import type { DialogProps } from '../../../app/registry/types';
import { LINEAR } from '../../../core/ease/index';
import type { Ease } from '../../../core/ease/types';
import { randomSeed } from '../../../core/math/rng';
import type { LineId } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type { Beat } from '../../../core/time/types';
import { Tools } from '../../../core/tools/index';
import { isNeutralRamp, RAMP_PROPS, type RampProp } from '../../../core/tools/clone';
import { rampExpression } from '../../../core/tools/ramp';
import type { CloneParams, RampSpec, ToolReport } from '../../../core/tools/types';
import { Checkbox } from '../../../ui/Checkbox';
import { IconButton } from '../../../ui/IconButton';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { TextField } from '../../../ui/TextField';
import type { Json } from '../../../core/util/types';
import { m } from '../batch.messages';
import s from '../Batch.module.css';
import { EXT, extObject, isBool, isNum, isStr, pick, ref, remember, toastNotes } from '../batchContext';
import {
  BeatInput,
  CurveField,
  ExpressionBox,
  NumberInput,
  ToolSheet,
  useBeatContext,
  useText,
  type SheetStatus,
} from '../sheetKit';
import { useToolGesture } from '../useToolGesture';
import { parsePattern } from './DistributeSheet';

const RAMP_LABEL: Readonly<Record<RampProp, string>> = {
  moveX: m.rampX,
  moveY: m.rampY,
  rotate: m.rampRotate,
  alpha: m.rampAlpha,
};

interface RampState {
  readonly from: number;
  readonly to: number;
  readonly curve: Ease;
  readonly pattern: string;
  readonly jitter: number;
}

const ZERO_RAMP: RampState = { from: 0, to: 0, curve: LINEAR, pattern: '', jitter: 0 };

/** Line label "#index name" in export order. */
export function lineLabel(index: number, name: string): string {
  return `#${String(index)} ${name}`;
}

export function CloneSheet(p: DialogProps<'clone'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const beatCtx = useBeatContext(services);
  const saved = useMemo(() => extObject(services, EXT.clone), [services]);
  const segments = useMemo(
    () => props.segments.filter((id) => store?.doc.keys.has(id) === true),
    [props.segments, store],
  );
  const notes = useMemo(() => props.notes.filter((id) => store?.doc.notes.has(id) === true), [props.notes, store]);
  // Lines in export order, read once (the preview may add lines).
  const [lines] = useState(() =>
    store === null
      ? []
      : store.doc.lineOrder.map((id, i) => ({ id, label: lineLabel(i, store.doc.lines.get(id)?.name ?? '') })),
  );
  const preselected = useMemo(() => {
    const sel = services.editor.getState().selection.lines;
    return lines.filter((l) => sel.has(l.id)).map((l) => l.id);
  }, [services, lines]);

  const [targetKind, setTargetKind] = useState<'lines' | 'create'>(
    preselected.length > 0 || lines.length > 0 ? 'lines' : 'create',
  );
  const [picked, setPicked] = useState<readonly LineId[]>(preselected);
  const [count, setCount] = useState(() => pick(saved, 'count', isNum, 4));
  const [namePattern, setNamePattern] = useState(() => pick(saved, 'namePattern', isStr, 'copy_{n}'));
  const [copyParent, setCopyParent] = useState(() => pick(saved, 'copyParent', isBool, true));
  const [copyZ, setCopyZ] = useState(() => pick(saved, 'copyZ', isBool, true));
  const [copyGroup, setCopyGroup] = useState(() => pick(saved, 'copyGroup', isBool, true));
  const [copyType, setCopyType] = useState(() => pick(saved, 'copyType', isBool, true));
  const [stagger, setStagger] = useState<Beat>(Beats.ZERO);
  const [ramps, setRamps] = useState<Readonly<Record<RampProp, RampState>>>({
    moveX: ZERO_RAMP,
    moveY: ZERO_RAMP,
    rotate: ZERO_RAMP,
    alpha: ZERO_RAMP,
  });
  const [conflict, setConflict] = useState<CloneParams['conflict']>(() => {
    const v = pick(saved, 'conflict', isStr, 'replace');
    return v === 'skip' || v === 'newLayer' ? v : 'replace';
  });
  const [includeNotes, setIncludeNotes] = useState(() => notes.length > 0 && pick(saved, 'includeNotes', isBool, true));
  const [keepSource, setKeepSource] = useState(() => pick(saved, 'keepSource', isBool, true));
  const [seed, setSeed] = useState(() => pick(saved, 'seed', isNum, 42));

  const setRamp = (prop: RampProp, patch: Partial<RampState>): void => {
    setRamps((r) => ({ ...r, [prop]: { ...r[prop], ...patch } }));
  };

  const rampSpecs = useMemo(() => {
    const out: Partial<Record<RampProp, RampSpec>> = {};
    let bad = false;
    for (const prop of RAMP_PROPS) {
      const r = ramps[prop];
      const pattern = parsePattern(r.pattern);
      if (pattern === null) bad = true;
      else out[prop] = { from: r.from, to: r.to, curve: r.curve, pattern, jitter: r.jitter };
    }
    return bad ? null : out;
  }, [ramps]);

  // Ramps in use as expressions over the targets (opacity stays whole, as the tool rounds it).
  const rampText = useMemo(() => {
    if (rampSpecs === null) return '';
    return RAMP_PROPS.flatMap((prop) => {
      const r = rampSpecs[prop];
      if (r === undefined || isNeutralRamp(r)) return [];
      const wrap = prop === 'alpha' ? (b: string): string => `round(${b})` : undefined;
      return [`${text.t(RAMP_LABEL[prop])}: ${rampExpression(r, 'add', wrap)}`];
    }).join('\n');
  }, [rampSpecs, text]);

  const params = useMemo<CloneParams | null>(() => {
    if (rampSpecs === null || (segments.length === 0 && notes.length === 0)) return null;
    const targets: CloneParams['targets'] =
      targetKind === 'lines'
        ? { kind: 'lines', lines: picked }
        : { kind: 'create', count, namePattern, copyParent, copyZ, copyGroup, copyType };
    if (targetKind === 'lines' && picked.length === 0) return null;
    return {
      sourceSegments: segments,
      sourceNotes: notes,
      targets,
      stagger,
      ramps: rampSpecs,
      conflict,
      includeNotes,
      keepSource,
      seed,
    };
  }, [
    rampSpecs,
    segments,
    notes,
    targetKind,
    picked,
    count,
    namePattern,
    copyParent,
    copyZ,
    copyGroup,
    copyType,
    stagger,
    conflict,
    includeNotes,
    keepSource,
    seed,
  ]);

  const build = useMemo(
    () => (params === null ? null : (tx: Parameters<typeof Tools.cloneToLines>[0]) => Tools.cloneToLines(tx, params)),
    [params],
  );
  const gesture = useToolGesture<ToolReport>({ services, label: ref(m.txnClone), command: 'tools.clone', build });

  const outcome = gesture.outcome;
  let status: SheetStatus = { kind: 'none' };
  if (rampSpecs === null) status = { kind: 'error', text: text.t(m.patternInvalid) };
  else if (targetKind === 'lines' && picked.length === 0) status = { kind: 'warn', text: text.t(m.clonePickTargets) };
  else if (gesture.status === 'paused') status = { kind: 'warn', text: text.t(m.previewPaused) };
  else if (outcome !== null && !outcome.ok) status = { kind: 'error', text: text.tr(outcome.reason) };
  else if (outcome?.ok === true) {
    const n = targetKind === 'lines' ? picked.length : count;
    status = { kind: 'ok', text: text.t(m.cloneStatus, { count: n }) };
  }

  const apply = (): void => {
    const out = gesture.apply();
    if (out === null || !out.ok) return;
    const value: Json = {
      count,
      namePattern,
      copyParent,
      copyZ,
      copyGroup,
      copyType,
      conflict,
      includeNotes,
      keepSource,
      seed,
    };
    remember(services, EXT.clone, value);
    const n = targetKind === 'lines' ? picked.length : count;
    services.ui.toast(ref(m.cloneDone, { count: n }), { kind: 'success', undo: out.txn !== null });
    toastNotes(services, out.notes);
    p.close({ applied: true });
  };
  const cancel = (): void => {
    gesture.cancel();
    p.close(null);
  };
  const toggleLine = (id: LineId, on: boolean): void => {
    setPicked((cur) => (on ? [...cur.filter((x) => x !== id), id] : cur.filter((x) => x !== id)));
  };

  return (
    <ToolSheet
      title={text.t(m.cloneTitle)}
      subtitle={text.t(m.countKeysNotes, { keys: segments.length, notes: notes.length })}
      status={status}
      applyLabel={text.t(m.apply)}
      applyDisabledReason={params === null ? ref(m.cloneNeedsTargets) : null}
      onApply={apply}
      onCancel={cancel}
    >
      <Segmented<'lines' | 'create'>
        label={text.t(m.cloneTargets)}
        value={targetKind}
        options={[
          { value: 'lines', label: text.t(m.cloneTargetLines) },
          { value: 'create', label: text.t(m.cloneTargetCreate) },
        ]}
        onChange={setTargetKind}
      />
      {targetKind === 'lines' ? (
        <div className={s.lineList} role="group" aria-label={text.t(m.cloneTargetLines)}>
          {lines.length === 0 && <p className={s.hint}>{text.t(m.cloneNoOtherLines)}</p>}
          {lines.map((l) => (
            <Checkbox
              className={s.check}
              key={l.id}
              label={l.id === props.line ? text.t(m.sourceLine, { line: l.label }) : l.label}
              checked={picked.includes(l.id)}
              onChange={(on) => {
                toggleLine(l.id, on);
              }}
            />
          ))}
        </div>
      ) : (
        <div className={s.group}>
          <div className={s.row}>
            <NumberInput label={text.t(m.cloneCount)} value={count} integer min={1} max={1000} onChange={setCount} />
            <TextField label={text.t(m.cloneNamePattern)} value={namePattern} onCommit={setNamePattern} />
          </div>
          <p className={s.hint}>{text.t(m.cloneNameHint)}</p>
          <Checkbox className={s.check} label={text.t(m.copyParent)} checked={copyParent} onChange={setCopyParent} />
          <Checkbox className={s.check} label={text.t(m.copyZ)} checked={copyZ} onChange={setCopyZ} />
          <Checkbox className={s.check} label={text.t(m.copyGroup)} checked={copyGroup} onChange={setCopyGroup} />
          <Checkbox className={s.check} label={text.t(m.copyType)} checked={copyType} onChange={setCopyType} />
        </div>
      )}
      <BeatInput label={text.t(m.cloneStagger)} value={stagger} ctx={beatCtx} onChange={setStagger} />
      <div className={s.group}>
        <p className={s.groupTitle}>{text.t(m.clonePerTarget)}</p>
        {RAMP_PROPS.map((prop) => (
          <div key={prop} className={s.ramp}>
            <span className={s.rampLabel}>{text.t(RAMP_LABEL[prop])}</span>
            <NumberInput
              label={text.t(m.rampFrom)}
              value={ramps[prop].from}
              onChange={(v) => {
                setRamp(prop, { from: v });
              }}
            />
            <NumberInput
              label={text.t(m.rampTo)}
              value={ramps[prop].to}
              onChange={(v) => {
                setRamp(prop, { to: v });
              }}
            />
          </div>
        ))}
        <Section title={text.t(m.rampMore)} collapsible defaultOpen={false}>
          {RAMP_PROPS.map((prop) => (
            <div key={prop} className={s.group}>
              <p className={s.groupTitle}>{text.t(RAMP_LABEL[prop])}</p>
              <CurveField
                services={services}
                label={text.t(m.curve)}
                value={ramps[prop].curve}
                onChange={(c) => {
                  setRamp(prop, { curve: c });
                }}
              />
              <div className={s.row}>
                <TextField
                  label={text.t(m.pattern)}
                  value={ramps[prop].pattern}
                  placeholder={ref(m.patternPlaceholder)}
                  onCommit={(v) => {
                    setRamp(prop, { pattern: v });
                  }}
                />
                <NumberInput
                  label={text.t(m.jitter)}
                  value={ramps[prop].jitter}
                  min={0}
                  onChange={(v) => {
                    setRamp(prop, { jitter: v });
                  }}
                />
              </div>
            </div>
          ))}
          <div className={s.rowTight}>
            <NumberInput label={text.t(m.seed)} value={seed} integer min={0} max={4294967295} onChange={setSeed} />
            <IconButton
              icon="RefreshCw"
              label={ref(m.newSeed)}
              onClick={() => {
                setSeed(randomSeed());
              }}
            />
          </div>
        </Section>
      </div>
      {rampText !== '' && (
        <ExpressionBox
          services={services}
          label={text.t(m.equivalentExpression)}
          code={rampText}
          note={text.t(m.cloneExpressionNote)}
        />
      )}
      <Segmented<CloneParams['conflict']>
        label={text.t(m.cloneOverlap)}
        value={conflict}
        options={[
          { value: 'replace', label: text.t(m.overlapReplace) },
          { value: 'skip', label: text.t(m.overlapSkip) },
          { value: 'newLayer', label: text.t(m.overlapNewLayer) },
        ]}
        onChange={setConflict}
      />
      <Checkbox
        className={s.check}
        label={text.t(m.cloneCopyNotes)}
        checked={includeNotes}
        onChange={setIncludeNotes}
        disabledReason={notes.length === 0 ? ref(m.cloneNoNotes) : null}
      />
      <Checkbox className={s.check} label={text.t(m.cloneKeepSource)} checked={keepSource} onChange={setKeepSource} />
    </ToolSheet>
  );
}
